import '@lmt/unicorn-shared/app/components/global-forms/GlobalFormContent.scss';
import { backendUri } from '@lmt/unicorn-shared/app/auth/openid/openid-settings';
import { OpenIdCallback } from '@lmt/unicorn-shared/app/auth/openid/OpenIdCallback';
import 'devextreme/dist/css/dx.light.css';
import { i18n } from 'i18next';
import React, { Suspense, useMemo } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { Provider } from 'react-redux';
import 'react-toastify/dist/ReactToastify.css';
import { PersistGate } from 'redux-persist/integration/react';
import { Loading } from './components/common/Loading';
import { FeatureFlags } from './constants/featureFlags';
import './helpers/cached-images';
import { MiniProfiler } from './helpers/MiniProfiler';
import { AppRouter } from './Router';
import { initializeQueryClient } from './initialize';
import { cleanupOnLogout, useClaims } from './features/auth/operations';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { config } from 'devextreme/common';
import { GenericEntrypoint } from './GenericEntrypoint';
import { configureUniStore } from './store/types';
import { useConfigureI18n } from './helpers/localization/init-i18next';
import { QuerySuspenseErrorWrapper } from './helpers/retry-helper';
config({
  licenseKey:
    'ewogICJmb3JtYXQiOiAxLAogICJjdXN0b21lcklkIjogImFhNTlmMjNiLTBhNzAtNDFkOS1iN2NlLWYxZjZlY2IzZDIxZiIsCiAgIm1heFZlcnNpb25BbGxvd2VkIjogMjQyCn0=.xEJ9VlSW0V1KKjrsRG1OL7TJ0N9jtvWrMU4iNAIb59H5+T4tZI0aMPUv3Yc/4qDAT8F1M2X6w7SwX170XUstvBVfHO2Kbuc57d6ngrNRttR3ljVVII+g8U/NK4SDAz466hojAg==',
});

export const App = (props: {
  appType: 'desktop' | 'web';
  additionalComponents?: React.FC;
  configureTranslation?: (translation: i18n) => void;
  backendStartFailed: boolean;
}) => {
  const queryClient = useMemo(() => {
    return initializeQueryClient();
  }, []);
  return (
    <OpenIdCallback
      signInRedirectHandler={(user) => {
        window.history.pushState(null, '', backendUri);
        queryClient.resetQueries();
      }}
      signOutRedirectHandler={() => {
        cleanupOnLogout();
      }}
      loading={<Loading loading={true} />}
    >
      <QueryClientProvider client={queryClient}>
        {/* <ReactQueryDevtools /> */}
        <Suspense fallback={<Loading flex={true} loading={true} />}>
          <InnerApp {...props} />
          {FeatureFlags.EnableMiniProfiler && <MiniProfiler />}
        </Suspense>
      </QueryClientProvider>
    </OpenIdCallback>
  );
};

/*
 * InnerApp is extracted from the <App> to be able to use react-query hooks
 * (which has to be nested under <QueryClientProvider />)
 */
const InnerApp = (props: {
  appType: 'desktop' | 'web';
  additionalComponents?: React.FC;
  configureTranslation?: (translation: i18n) => void;
  backendStartFailed: boolean;
}) => {
  const userId = useClaims()?.id || 'root';
  const username = useClaims()?.name || 'root';
  const { store, persistor } = useMemo(() => {
    return configureUniStore(userId, username);
  }, [userId, username]);

  // this needs to stay on top so that AppRouter reloads when localization is loaded
  useConfigureI18n(props.configureTranslation);

  return (
    <Provider store={store} key={userId}>
      <PersistGate loading={<Loading loading={true} />} persistor={persistor}>
        <GenericEntrypoint>
          <QuerySuspenseErrorWrapper isOutsideRouter={true}>
            <AppRouter key={userId} {...props} />
          </QuerySuspenseErrorWrapper>
        </GenericEntrypoint>
      </PersistGate>
    </Provider>
  );
};
